import type { ReactNode } from "react";

import { NoAccessView } from "@/shared/components/NoAccessView";

import { findEntryByTo, findRouteGroup, isEntryRoutable, isRouteGroupAccessible } from "./resolveNavAccess";
import { useNavContext } from "./useNavContext";

export interface RequireAccessProps {
  /** The entry's `to` in `NAVIGATION_REGISTRY`. Same key the sidebar renders. */
  to: string;
  children: ReactNode;
  fallback?: ReactNode;
}

export interface RequireRouteAccessProps {
  /** The mounted route's pattern — the `path` shared by every entry it serves (e.g. `"cards/*"`). */
  path: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Guards ONE destination, against exactly its own entry.
 *
 * This is the guard for a leaf route. It is deliberately strict: entering the cards module through Holders
 * must not grant Cards, whose requirement is a different module. Use it everywhere except the wildcard routes
 * that host several surfaces — see {@link RequireRouteAccess}.
 *
 * Denial renders `NoAccessView` rather than redirecting. That is the idiom the rest of the dashboard already
 * uses for a backend 403, and it cannot loop — a redirect target can itself be denied, which is exactly the
 * trap `OrgModuleBoundary` documented in its fallback.
 *
 * It is an affordance guard, not the security boundary: the API decides, and the per-module 401/403 latch
 * remains the last line of defense.
 */
export const RequireAccess = ({ to, children, fallback }: RequireAccessProps) => {
  const ctx = useNavContext();
  const entry = findEntryByTo(to);

  // An unregistered target is a wiring mistake, not a permission decision. Fail closed and say so.
  if (!entry) {
    if (import.meta.env.DEV) {
      console.error(`[RequireAccess] "${to}" is not in NAVIGATION_REGISTRY — add the entry there.`);
    }
    return fallback ?? <NoAccessView />;
  }

  if (!ctx) return null;

  return isEntryRoutable(entry, ctx) ? children : (fallback ?? <NoAccessView />);
};

/**
 * Guards a MOUNTED ROUTE that serves several surfaces, against any of them.
 *
 * `cards/*` renders `CardsNavigation`, which routes to cards, holders and pockets — three entries with three
 * different requirements. Requiring all of them would lock a user out of the whole module for lacking
 * `wallets:read` when they can perfectly well use cards and holders, so this admits on ANY.
 *
 * Opening the parent is NOT permission to open every surface inside it. Each sub-route carries its own
 * {@link RequireAccess}, and the module's index redirect resolves to the first surface the user can actually
 * reach. Without that pairing, ANY here would be a hole.
 */
export const RequireRouteAccess = ({ path, children, fallback }: RequireRouteAccessProps) => {
  const ctx = useNavContext();

  // An empty group is a wiring mistake — no entry declares this route pattern — not a permission
  // decision. Fail closed and say so, the same way RequireAccess does for an unknown destination.
  if (findRouteGroup(path).length === 0) {
    if (import.meta.env.DEV) {
      console.error(`[RequireRouteAccess] no NAVIGATION_REGISTRY entry has path "${path}".`);
    }
    return fallback ?? <NoAccessView />;
  }

  if (!ctx) return null;

  return isRouteGroupAccessible(path, ctx) ? children : (fallback ?? <NoAccessView />);
};
